import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Check, X, Crown, Sparkles, ArrowRight, ShieldCheck, Star,
  CreditCard, Loader2, Calculator, LineChart, NotebookPen, History,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEOHead } from '../hooks/usePageSEO';
import { useSoloAnnualPromo } from '../hooks/useSoloAnnualPromo';
import { SoloAnnualPromoCard } from '../components/shared';

/* Bouton de navigation avec un court état de chargement (spinner) — confirmation visuelle
   immédiate pour les CTA de paiement/inscription, avant que la page suivante ne s'affiche. */
const NavButton = ({ to, className, testid, children }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  return (
    <button
      onClick={() => { if (loading) return; setLoading(true); setTimeout(() => navigate(to), 350); }}
      disabled={loading}
      className={className}
      data-testid={testid}
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : children}
    </button>
  );
};

/* ---------------- Données ---------------- */

// Ce qui est inclus dans la version gratuite de l'app, à vie — visible avant tout
// paiement pour poser clairement la valeur de départ de Body-Up.
export const FREE_TIER = {
  name: ['Version gratuite', 'Free version'],
  tag: ['Toujours gratuit, sans carte bancaire', 'Always free, no credit card'],
  features: [
    { Icon: Calculator, label: ['Calculateur de calories & macros complet', 'Full calorie & macro calculator'] },
    { Icon: LineChart, label: ['Suivi du poids & des mensurations (avatar interactif)', 'Weight & measurements tracking (interactive avatar)'] },
    { Icon: NotebookPen, label: ['Journal alimentaire manuel', 'Manual food journal'] },
    { Icon: History, label: ['Historique des 7 derniers jours', 'Last 7 days history'] },
  ],
};

export const COACHING_TIERS = [
  {
    key: 'suivi', name: ['Suivi Pro', 'Suivi Pro'], price: '9.90', unit: ['/ mois', '/ month'],
    alt: ['ou 79.- / an (2 mois offerts)', 'or 79.- / year (2 months free)'],
    tag: ['App · Tracking', 'App · Tracking'], accent: 'yellow', to: '/suivi-pro', cta: ['Essayer 7 jours gratuits', 'Try 7 days free'],
    trial: true,
    desc: ['Trackez vos calories au quotidien dans l\'app, sans effort.', 'Track your calories daily in the app, effortlessly.'],
    features: [
      ['Accès à l\'app Body-Up pour le tracking', 'Body-Up app access for tracking'],
      ['📸 Scan IA du plat → macros estimées', '📸 AI meal scan → estimated macros'],
      ['Recherche AI illimitée des aliments', 'Unlimited AI food search'],
      ['180 recettes hyperprotéinées incluses', '180 high-protein recipes included'],
      ['Créez vos exercices persos avec l\'IA (texte + média)', 'Create your own exercises with AI (text + media)'],
      ['Votre plan alimentaire (si acheté)', 'Your meal plan (if purchased)'],
      ['Historique de journal illimité', 'Unlimited journal history'],
    ],
  },
  {
    key: 'solo', name: ['Suivi Pro+', 'Suivi Pro+'], price: '19.90', unit: ['/ mois', '/ month'],
    alt: ['ou 199.- / an (2 mois offerts) · sans engagement', 'or 199.- / year (2 months free) · no commitment'],
    tag: ['App · Musculation & Cardio DIY', 'App · DIY Training & Cardio'], accent: 'blue', popular: true, to: '/checkout/solo', cta: ['Essayer 7 jours gratuits', 'Try 7 days free'],
    trial: true,
    desc: ['Tout le Suivi Pro + musculation ET cardio complets, en autonomie.', 'All of Suivi Pro + full training AND cardio, on your own.'],
    features: [
      ['Tout du Suivi Pro inclus', 'Everything in Suivi Pro'],
      ['Bibliothèque de 449 exercices (muscu + cardio, vidéos Sora 2)', '449-exercise library (training + cardio, Sora 2 videos)'],
      ['Programme par IA, manuel (débutant ou avancé)', 'AI-built or manual program (beginner or advanced)'],
      ["Coach IA pour la sélection d'exercices (à l'usage)", 'AI coach for exercise selection (pay-per-use)'],
      ['Idéal pour créer votre programme vous-même', 'Ideal to build your own program'],
    ],
  },
  {
    key: 'communaute', name: ['Communauté Body-Up', 'Body-Up Community'], price: '49', unit: ['/ mois', '/ month'],
    alt: ['Sans engagement · 100% en ligne', 'No commitment · 100% online'],
    tag: ['Coaching app', 'App coaching'], accent: 'pink', to: '/checkout/communaute', cta: ['Rejoindre la Communauté', 'Join the Community'],
    desc: ['Programmes & plans personnalisés + messagerie coach, via l\'app.', 'Personalized programs & plans + coach messaging, via the app.'],
    features: [
      ['Tout du Suivi Pro+ inclus (muscu, cardio, recettes...)', 'Everything in Suivi Pro+ (training, cardio, recipes...)'],
      ['Programme de musculation & cardio perso (par Laurent)', 'Custom training & cardio program (by Laurent)'],
      ['Plan alimentaire perso renouvelé chaque mois', 'Personalized meal plan refreshed monthly'],
      ['Messagerie directe avec le coach (app)', 'Direct coach messaging (app)'],
      ['Toutes les fonctions premium de l\'app', 'All premium app features'],
    ],
  },
  {
    key: 'transfo', name: ['Pack Transformation', 'Transformation Pack'], price: '299', unit: ['/ mois', '/ month'],
    alt: ['Engagement 6 mois · -5% en 1x', '6-month commitment · -5% upfront'],
    tag: ['Coaching 100% en ligne', '100% online coaching'], accent: 'purple', premium: true, to: '/transformation', cta: ['Découvrir le coaching', 'Discover coaching'],
    paymentCatId: 'coaching-online', paymentOption: 'monthly',
    desc: ['Vrai accompagnement humain avec Laurent, sur 6 mois.', 'Real human coaching with Laurent, over 6 months.'],
    features: [
      ['Tout de la Communauté inclus', 'Everything in Community'],
      ['Onboarding personnalisé avec Laurent', 'Personalized onboarding with Laurent'],
      ['6 visios Zoom 30 min incluses (≈1/mois)', '6 included 30-min Zoom calls (≈1/month)'],
      ['Programme de muscu & cardio sur-mesure chaque mois', 'Custom training & cardio program every month'],
      ['Messagerie 24/7 + ajustements réguliers', '24/7 messaging + regular adjustments'],
    ],
  },
  {
    key: 'vip', name: ['VIP Hybrid', 'VIP Hybrid'], price: '399', unit: ['/ mois', '/ month'],
    alt: ['Engagement 6 mois · -5% en 1x', '6-month commitment · -5% upfront'],
    tag: ['Hybride présentiel', 'Hybrid in-person'], accent: 'purple', premium: true, to: '/transformation', cta: ['Découvrir le VIP', 'Discover VIP'],
    paymentCatId: 'coaching-hybride', paymentOption: 'vip_monthly',
    desc: ['Le meilleur du coaching en ligne + le présentiel à Nyon.', 'The best of online coaching + in-person in Nyon.'],
    features: [
      ['Tout du Pack Transformation (hors les 6 visios Zoom)', 'Everything in Transformation (except the 6 Zoom calls)'],
      ['6 séances 60\' au choix : studio Nyon OU Zoom', '6× 60\' sessions, your choice: Nyon studio OR Zoom'],
      ['2 scans 3D complets (début + fin)', '2 full 3D scans (start + end)'],
      ['Expérience premium & accès salle Body-Up', 'Premium experience & Body-Up studio access'],
    ],
  },
];

// Comparatif des 5 abonnements [suivi, solo, communaute, transfo, vip]
export const COACHING_COMPARE = [
  { label: ['Accès à l\'app Body-Up', 'Body-Up app access'], v: [true, true, true, true, true] },
  { label: ['Suivi des calories & macros', 'Calories & macros tracking'], v: [true, true, true, true, true] },
  { label: ['Scan IA des plats (photo)', 'AI meal scan (photo)'], v: [true, true, true, true, true] },
  { label: ['Recherche AI illimitée', 'Unlimited AI search'], v: [true, true, true, true, true] },
  { label: ['180 recettes hyperprotéinées', '180 high-protein recipes'], v: [true, true, true, true, true] },
  { label: ['Créateur d\'exercices persos (IA texte + média)', 'Custom exercise creator (AI text + media)'], v: [true, true, true, true, true] },
  { label: ['Bibliothèque muscu + cardio (449 ex.) + programmes IA/manuel', 'Training + cardio library (449 ex.) + AI/manual programs'], v: [false, true, true, true, true] },
  { label: ["Coach IA pour la sélection d'exercices", 'AI coach for exercise selection'], v: [false, ['À l\'usage', 'Pay-per-use'], ['1 inclus/mois', '1 included/month'], ['5 inclus/mois', '5 included/month'], ['5 inclus/mois', '5 included/month']] },
  { label: ['Essai gratuit 7 jours', '7-day free trial'], v: [true, true, false, false, false] },
  { label: ['Programme de musculation & cardio perso (Laurent)', 'Custom training & cardio program (Laurent)'], v: [false, false, true, true, true] },
  { label: ['Plan alimentaire perso renouvelé /mois', 'Personalized meal plan refreshed /month'], v: [false, false, true, true, true] },
  { label: ['Messagerie directe avec le coach', 'Direct coach messaging'], v: [false, false, true, true, true] },
  { label: ['Onboarding personnalisé avec Laurent', 'Personalized onboarding with Laurent'], v: [false, false, false, true, true] },
  { label: ['Visios Zoom 30 min incluses', '30-min Zoom calls included'], v: [false, false, false, ['6 total', '6 total'], false] },
  { label: ['Suivi mensuel & ajustements', 'Monthly review & adjustments'], v: [false, false, false, true, true] },
  { label: ['Séances studio Nyon ou Zoom au choix (60\')', 'Studio Nyon or Zoom sessions of your choice (60\')'], v: [false, false, false, false, ['6 total', '6 total']] },
  { label: ['Scan 3D complet', 'Full 3D scan'], v: [false, false, false, false, ['2 scans', '2 scans']] },
  { label: ['Engagement', 'Commitment'], v: [['Mensuel', 'Monthly'], ['Mensuel', 'Monthly'], ['Mensuel', 'Monthly'], ['6 mois', '6 months'], ['6 mois', '6 months']] },
];

const ACCENTS = {
  zinc: { border: 'border-white/10', ring: '', badge: 'bg-zinc-700/40 text-zinc-300', btn: 'from-zinc-700 to-zinc-600 text-white', check: 'text-zinc-400' },
  pink: { border: 'border-primary/40', ring: '', badge: 'bg-primary/15 text-primary', btn: 'from-primary to-pink-500 text-white', check: 'text-primary' },
  yellow: { border: 'border-yellow-500/40', ring: '', badge: 'bg-yellow-500/15 text-yellow-300', btn: 'from-yellow-400 to-primary text-black', check: 'text-yellow-400' },
  blue: { border: 'border-sky-500/40', ring: '', badge: 'bg-sky-500/15 text-sky-300', btn: 'from-sky-500 to-primary text-white', check: 'text-sky-400' },
  purple: { border: 'border-purple-500/50', ring: 'shadow-[0_0_40px_-12px_rgba(168,85,247,0.5)]', badge: 'bg-purple-500/15 text-purple-300', btn: 'from-purple-500 to-primary text-white', check: 'text-purple-400' },
};

/* ---------------- Composants ---------------- */

export const Cell = ({ val, en }) => {
  if (val === true) return <Check className="w-4 h-4 text-emerald-400 mx-auto" />;
  if (val === false) return <X className="w-4 h-4 text-zinc-700 mx-auto" />;
  return <span className="text-xs font-bold text-white">{val[en ? 1 : 0]}</span>;
};

export const OfferCard = ({ t, en, L, index }) => {
  const a = ACCENTS[t.accent];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.06, duration: 0.35 }}
      className={`relative rounded-3xl p-6 border ${a.border} ${a.ring} bg-gradient-to-br from-zinc-900 to-black flex flex-col`}
      data-testid={`offre-card-${t.key}`}
    >
      {t.best && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-white bg-gradient-to-r from-primary to-pink-500 whitespace-nowrap">{L('LE PLUS COMPLET', 'MOST COMPLETE')}</span>}
      {t.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-black bg-gradient-to-r from-yellow-400 to-amber-500 whitespace-nowrap inline-flex items-center gap-1"><Star className="w-2.5 h-2.5 fill-black" /> {L('LE PLUS POPULAIRE', 'MOST POPULAR')}</span>}
      {t.premium && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-bold tracking-wider text-white bg-gradient-to-r from-purple-500 to-primary whitespace-nowrap inline-flex items-center gap-1"><Crown className="w-2.5 h-2.5" /> PREMIUM</span>}
      <span className={`inline-block self-start px-2.5 py-0.5 rounded-full text-[10px] font-bold mb-2 ${a.badge}`}>{t.tag[en ? 1 : 0]}</span>
      <h3 className="font-bold text-white text-xl leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name[en ? 1 : 0]}</h3>
      <div className="flex items-end gap-1 mt-2">
        <span className="text-3xl font-extrabold text-white">{t.price}</span>
        <span className="text-zinc-400 text-sm mb-1 font-semibold">CHF {t.unit[en ? 1 : 0]}</span>
      </div>
      <p className="text-[11px] text-zinc-500 font-semibold mb-1 min-h-[14px]">{t.alt ? t.alt[en ? 1 : 0] : ''}</p>
      {t.trial && (
        <span className="inline-flex self-start items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 mb-2" data-testid={`offre-trial-badge-${t.key}`}>
          🎁 {L('7 jours gratuits', '7-day free trial')}
        </span>
      )}
      {t.desc && <p className="text-sm text-zinc-400 mb-4 mt-1">{t.desc[en ? 1 : 0]}</p>}
      <ul className={`space-y-2 mb-6 text-sm ${t.desc ? '' : 'mt-3'}`}>
        {t.features.map((f) => (
          <li key={f[0]} className="flex items-start gap-2 text-zinc-300"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${a.check}`} /> {f[en ? 1 : 0]}</li>
        ))}
      </ul>
      {t.paymentCatId ? (
        <div className="mt-auto flex flex-col gap-2">
          <Link
            to={`/postuler-coaching?type=${t.paymentCatId === 'coaching-hybride' ? 'hybride' : 'online'}`}
            className={`w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold bg-gradient-to-r ${a.btn} hover:scale-[1.02] transition-transform`}
            data-testid={`offre-cta-postuler-${t.key}`}
          >
            <Sparkles className="w-4 h-4" /> {L('Postuler pour un appel', 'Apply for a call')}
          </Link>
          <NavButton
            to={`/transformation?autocheckout=${t.paymentOption}`}
            className="w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold text-white bg-white/5 border-2 border-white/15 hover:border-white/40 transition-all"
            testid={`offre-cta-pay-${t.key}`}
          >
            <CreditCard className="w-4 h-4" /> {L('Payer maintenant', 'Pay now')}
          </NavButton>
        </div>
      ) : (
        <NavButton to={t.to} className={`mt-auto w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-bold bg-gradient-to-r ${a.btn} hover:scale-[1.02] transition-transform`} testid={`offre-cta-${t.key}`}>
          {t.cta[en ? 1 : 0]} <ArrowRight className="w-3.5 h-3.5" />
        </NavButton>
      )}
    </motion.div>
  );
};

/* ---------------- Page ---------------- */

const OffresPage = () => {
  const { language } = useLanguage();
  const en = language === 'en';
  const L = (fr, eng) => (en ? eng : fr);
  const promo = useSoloAnnualPromo();
  const tiers = promo?.active
    ? COACHING_TIERS.map((t) => (t.key === 'solo' ? {
        ...t,
        alt: [
          `🔥 -${promo.discountPct}% : ${promo.price}.- / an au lieu de ${promo.originalPrice}.- (jusqu'au 31 oct.) · sans engagement`,
          `🔥 -${promo.discountPct}%: CHF ${promo.price} / yr instead of ${promo.originalPrice} (until Oct 31) · no commitment`,
        ],
      } : t))
    : COACHING_TIERS;

  return (
    <>
      <SEOHead title={L('Nos offres & tarifs | Body-Up', 'Plans & pricing | Body-Up')} description={L('Le Coaching en ligne & hybride Body-Up : de la version gratuite de l\'app jusqu\'au coaching présentiel à Nyon.', 'Body-Up online & hybrid coaching: from the free app to in-person coaching in Nyon.')} path="/offres" />
      <main className="min-h-screen bg-black pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* WOW animated background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute -top-20 left-1/4 w-[28rem] h-[28rem] bg-primary/20 rounded-full blur-[120px] animate-pulse" />
          <div className="absolute top-1/3 -right-24 w-[26rem] h-[26rem] bg-purple-600/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1.2s' }} />
          <div className="absolute bottom-0 left-1/3 w-[24rem] h-[24rem] bg-pink-500/15 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }} />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        </div>
        <div className="max-w-6xl mx-auto relative z-10">
          {/* Hero */}
          <div className="text-center mb-8">
            <motion.h1
              initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
              className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {L('Coaching & app,', 'Coaching & app,')}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-pink-500 to-purple-400">{L('un seul objectif', 'one goal')}</span>
            </motion.h1>
            <p className="text-zinc-400 text-sm sm:text-base mt-2 max-w-2xl mx-auto">
              {L('De la version gratuite de l\'app jusqu\'au coaching présentiel à Nyon avec Laurent.', 'From the free app to in-person coaching in Nyon with Laurent.')}
            </p>
          </div>

          <SoloAnnualPromoCard promo={promo} language={language} ctaHref="/checkout/solo" />

          {/* Version gratuite — toujours visible, avant tout paiement */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
            className="rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-900/80 to-black p-5 sm:p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
            data-testid="offre-free-tier-card"
          >
            <div className="w-12 h-12 rounded-2xl bg-zinc-800 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap mb-1.5">
                <h2 className="font-bold text-white text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{FREE_TIER.name[en ? 1 : 0]}</h2>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">{FREE_TIER.tag[en ? 1 : 0]}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                {FREE_TIER.features.map((f) => (
                  <div key={f.label[0]} className="flex items-center gap-2 text-sm text-zinc-300">
                    <f.Icon className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {f.label[en ? 1 : 0]}
                  </div>
                ))}
              </div>
            </div>
            <Link to="/calculateur-calories" className="shrink-0 self-stretch sm:self-center inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-white/5 border-2 border-white/15 hover:border-white/40 transition-all text-sm" data-testid="offre-free-tier-cta">
              {L('Commencer gratuitement', 'Start for free')} <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-12">
            {tiers.map((t, i) => <OfferCard key={t.key} t={t} en={en} L={L} index={i} />)}
          </div>

          {/* Comparatif détaillé des abonnements */}
          <h3 className="text-xl sm:text-2xl font-bold text-white text-center mb-5" style={{ fontFamily: 'Outfit, sans-serif' }}>{L('Comparez les abonnements en détail', 'Compare subscriptions in detail')}</h3>
          <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="offres-coaching-comparison">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="bg-zinc-900/80">
                  <th className="text-left px-4 py-4 font-semibold text-zinc-400 sticky left-0 bg-zinc-900/80">{L('Ce qui est inclus', 'What\'s included')}</th>
                  {COACHING_TIERS.map((t) => (
                    <th key={t.key} className="px-3 py-4 text-center">
                      <div className="font-bold text-white text-xs sm:text-sm">{t.name[en ? 1 : 0]}</div>
                      <div className="text-[11px] text-zinc-500 font-mono">{t.price} {t.unit[en ? 1 : 0]}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COACHING_COMPARE.map((f, ri) => (
                  <tr key={ri} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="text-left px-4 py-3 text-zinc-300 sticky left-0 bg-black">{f.label[en ? 1 : 0]}</td>
                    {f.v.map((val, ci) => (<td key={ci} className="px-3 py-3 text-center"><Cell val={val} en={en} /></td>))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6">
              <Star className="w-6 h-6 text-yellow-400 mb-2" />
              <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{L('4.9/5 · Studio Body-Up Nyon', '4.9/5 · Body-Up Studio Nyon')}</h3>
              <p className="text-zinc-400 text-sm mt-1">{L('Plus de 10 ans d\'accompagnement, des centaines de transformations réelles.', '10+ years of coaching, hundreds of real transformations.')}</p>
            </div>
            <div className="rounded-2xl border border-purple-500/40 bg-gradient-to-br from-purple-500/10 to-black p-6 flex flex-col">
              <Crown className="w-6 h-6 text-purple-300 mb-2" />
              <h3 className="font-bold text-white text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{L('Prêt pour un vrai accompagnement ?', 'Ready for real coaching?')}</h3>
              <p className="text-zinc-400 text-sm mt-1 mb-4">{L('Pack Transformation (299 CHF/mois) ou VIP Hybrid présentiel (399 CHF/mois) : Laurent à vos côtés pendant 6 mois.', 'Transformation (299 CHF/mo) or in-person VIP Hybrid (399 CHF/mo): Laurent by your side for 6 months.')}</p>
              <Link to="/transformation" className="mt-auto self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-white bg-gradient-to-r from-purple-500 to-primary hover:scale-[1.02] transition-transform" data-testid="offres-transfo-cta">
                {L('Découvrir le coaching', 'Discover coaching')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <p className="text-center text-xs text-zinc-500 mt-10 inline-flex items-center justify-center gap-2 w-full">
            <ShieldCheck className="w-3.5 h-3.5 text-green-400" /> {L('Paiement sécurisé Stripe · Abonnements app & coaching flexibles, sans engagement', 'Secure Stripe payment · Flexible app & coaching subscriptions, no commitment')}
          </p>
        </div>
      </main>
    </>
  );
};

export default OffresPage;
